// Module Import
//---------------------------------
import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Link } from 'gatsby-link'
import TypographyAtom from '../../atoms/typography/typography'
import ImageCut from '../../../assets/images/graphic_imageCut.svg'
import Image from '../../atoms/images/images'
import SaleLabel from '../SaleLabel/SaleLabel'
import { brandIvory, brandOrange, brandWhite } from '../../../assets/consts/color'
import Loader from '../../atoms/loader/Loader'

// Component Import
//---------------------------------

// Styled Component Declarations
//---------------------------------
const ImageFilter = styled.div`
  border-radius: 4px;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  z-index: 2;
  background-color: ${brandOrange};
  mix-blend-mode: overlay;
  opacity: 0;
  transition: 0.25s;
`

const ImageWrapper = styled.div`
  background-color: ${brandWhite};
  position: relative;
  overflow: hidden;
  border-radius: 4px;

  &:before {
    background-image: url(${ImageCut});
    background-position: top left;
    background-size: cover;
    content: '';
    height: 30px;
    left: -1px;
    position: absolute;
    top: -1px;
    transform: rotate(180deg);
    width: 30px;
    z-index: 1;
  }
`
const PriceLabel = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  border-radius: 8px;
  background-color: ${brandIvory};

  p {
    padding: 0;

    &:not(:last-of-type) {
      margin-right: 4px;
    }
  }

  p:nth-child(2) {
    text-decoration: line-through;
  }
`
const CopyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 40px 0 0;
  position: relative;
`

const ProductCardWrapper = styled(Link)`
  text-decoration: none;
  flex-direction: column;
  display: flex;
  gap: 24px;
  height: 100%;
  position: relative;

  h4 {
    margin-top: 0;
  }

  &:hover ${ImageFilter} {
    opacity: 1;
  }

  p {
    overflow: hidden;
    display: -webkit-box;

    -webkit-box-orient: vertical;
  }
`
const RelatedProductWrapper = styled(Link)`
  display: flex;
  position: relative;
  text-decoration: none;
  gap: 24px;

  & > div {
    padding: 0;
  }

  &:hover ${ImageFilter} {
    opacity: 1;
  }

  .price-and-stock {
    gap: 5px;
    p {
      font-size: 14px;
    }

    div {
      padding: 0;
    }

    ${PriceLabel} {
      background-color: transparent;
    }
  }

  .stock-label {
    color: ${brandOrange};
  }

  .product-title {
    font-size: 18px;
  }

  .sale-label {
    padding: 4px;

    p {
      font-size: 14px;
    }
  }

  ${CopyWrapper} {
    gap: 12px;
  }
`

const Footer = styled.div`
  display: flex;
  gap: 16px;
  flex-direction: row;
  align-items: end;
  flex-grow: 1;
  justify-content: space-between;
  margin-top: auto;

  p {
    padding: 8px 16px;
    border-radius: 8px;
    background-color: ${brandIvory};
  }
`

// Component Export
//---------------------------------
export default function ProductCard({
  relatedProduct,
  productTitle,
  productDescription,
  altTag,
  imgSrc,
  productLink,
  productPrice,
  compareAtPrice,
  stockLevel,
  hidePrice,
  isRental,
  nonFooter,
  isNotify,
  carousel,
  isLoading,
}) {
  // clean spaces from path and convert to '-'
  const slugifiedPath = productLink.toLowerCase().replace(/\s/g, '-')
  const description =
    productDescription?.length > 600
      ? `${productDescription?.substring(0, 600)}...`
      : productDescription

  const priceSaving = compareAtPrice - productPrice || 0

  const isPriceComparingShown = compareAtPrice && priceSaving > 0

  if (productTitle === 'DJI Mavic 3 Multispectral') {
  }

  const getStockText = useCallback(() => {
    if (stockLevel === null) {
      return ''
    }

    if (isNotify || stockLevel <= 0) {
      return 'Out of stock'
    }

    if (stockLevel > 5) {
      return '5+ items in stock'
    }

    return `${stockLevel} items in stock`
  }, [stockLevel])

  if (relatedProduct) {
    return (
      <RelatedProductWrapper to={slugifiedPath}>
        <div className="col-xs-4 col-md-4">
          <ImageWrapper>
            {!!isPriceComparingShown && !hidePrice && <SaleLabel top={0} right={0} />}
            <Image imgSrc={imgSrc} altTag={altTag} hasFrame="No Frame" aspectRatio="1/1" />
            <ImageFilter />
          </ImageWrapper>
        </div>
        <div className="col-xs-8 col-md-8">
          <CopyWrapper>
            <TypographyAtom className="product-title" type="H4" weight="Bold" copy={productTitle} />
            <div className="flex middle-xs price-and-stock">
              <PriceLabel>
                <TypographyAtom
                  type="P"
                  weight="Regular"
                  color={hidePrice && 'Orange'}
                  copy={`${
                    hidePrice
                      ? 'Ask Us'
                      : Intl.NumberFormat('en-GB', {
                          style: 'currency',
                          currency: 'GBP',
                        }).format(productPrice)
                  } ${!hidePrice && isRental && productTitle !== 'Drone Rental Deposit' ? 'weekly' : ''}`}
                />
                {!!isPriceComparingShown && !hidePrice && (
                  <>
                    <TypographyAtom
                      type="P"
                      weight="Regular"
                      color="Grey"
                      copy={`${Intl.NumberFormat('en-GB', {
                        style: 'currency',
                        currency: 'GBP',
                      }).format(parseFloat(compareAtPrice))}`}
                    />
                    <TypographyAtom
                      type="P"
                      weight="Regular"
                      color="Orange"
                      copy={`Save ${Intl.NumberFormat('en-GB', {
                        style: 'currency',
                        currency: 'GBP',
                      }).format(parseFloat(priceSaving))}`}
                    />
                  </>
                )}
              </PriceLabel>
            </div>
          </CopyWrapper>
        </div>
      </RelatedProductWrapper>
    )
  }

  return (
    <ProductCardWrapper to={slugifiedPath} tabIndex={carousel ? -1 : 0}>
      <ImageWrapper>
        {!!isPriceComparingShown && !hidePrice && <SaleLabel top={0} right={0} />}
        <Image imgSrc={imgSrc} altTag={altTag} hasFrame="Bottom Top Clip" aspectRatio="1/1" />
        <ImageFilter />
      </ImageWrapper>
      <CopyWrapper>
        <TypographyAtom type="H4" weight="Bold" copy={productTitle} />
        <TypographyAtom type="P" weight="Regular" copy={description} />
      </CopyWrapper>
      {!nonFooter && (
        <Footer>
          <PriceLabel>
            <TypographyAtom
              type="P"
              weight="Regular"
              copy={
                hidePrice
                  ? 'Price on Request'
                  : `${Intl.NumberFormat('en-GB', {
                      style: 'currency',
                      currency: 'GBP',
                    }).format(
                      productPrice
                    )} ${isRental && productTitle !== 'Drone Rental Deposit' ? 'weekly' : ''}`
              }
            />
            {!!isPriceComparingShown && !hidePrice && (
              <>
                <TypographyAtom
                  type="P"
                  weight="Regular"
                  color="Grey"
                  copy={`${Intl.NumberFormat('en-GB', {
                    style: 'currency',
                    currency: 'GBP',
                  }).format(parseFloat(compareAtPrice))}`}
                />
                <TypographyAtom
                  type="P"
                  weight="Regular"
                  color="Orange"
                  copy={`Save ${Intl.NumberFormat('en-GB', {
                    style: 'currency',
                    currency: 'GBP',
                  }).format(parseFloat(priceSaving))}`}
                />
              </>
            )}
          </PriceLabel>
          {stockLevel !== null &&
              (!hidePrice || hidePrice && stockLevel > 0) &&
            (isLoading ? (
              <Loader />
            ) : (
              <TypographyAtom type="P" weight="Regular" copy={getStockText()} />
            ))}
        </Footer>
      )}
    </ProductCardWrapper>
  )
}

ProductCard.propTypes = {
  productTitle: PropTypes.string.isRequired,
  productDescription: PropTypes.string.isRequired,
  imgSrc: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.object.isRequired]),
  altTag: PropTypes.string,
  hidePrice: PropTypes.bool,
  productLink: PropTypes.string.isRequired,
}
